import { SvgIconComponent } from "@mui/icons-material";
import {
  CalendarMonthRounded,
  CompareArrowsRounded,
  InsightsRounded,
  TrackChangesRounded,
  TravelExploreRounded,
} from "@mui/icons-material";

/**
 * Starter prompts for the chat landing.
 *
 * These are PURE UI. A preset is just a string handed to the same `onStart`
 * the composer calls, so the first message is byte-identical to one the user
 * typed. No preset ids, no seeded conversations, nothing on the server that
 * has to be kept in step with this file. Editing the copy here is the entire
 * deployment story, and the API cannot tell the difference.
 */
export interface ChatPreset {
  /** Sent verbatim as the first message. */
  prompt: string;
  icon: SvgIconComponent;
}

/**
 * Keep every prompt ANSWERABLE. A preset that returns "I couldn't find that"
 * is worse than no preset: it is a live demo of the product failing, on the
 * first screen, to a user deciding whether to keep paying. Prefer large,
 * well-covered companies and topics the tools genuinely reach.
 */

/**
 * The two things nothing else does. Both search the transcript corpus, one
 * across every company and one inside a single company's calls.
 *
 * Three of the four cards on the landing are drawn, and TWO of those three
 * always come from here (Ben, 2026-07-31). An even draw across every
 * capability spent a third of the screen on questions any finance app can
 * answer, and the cards are the only place most users will ever see what makes
 * this one different.
 */
const SHOWCASE_POOL: ChatPreset[][] = [
  // No ticker: the theme sweep. The hardest capability to guess exists, so it
  // is guaranteed a slot on every visit.
  [
    {
      prompt: "Which companies are talking about AI capex?",
      icon: TravelExploreRounded,
    },
    // A known-good retrieval case: "laser" and "drone" are both rare enough
    // (0.8% and 0.3% of the corpus) for the keyword arm's IDF gate to let them
    // through, so this hits the hybrid path it is meant to demonstrate.
    {
      prompt: "Are there any companies making lasers to counter drones?",
      icon: TravelExploreRounded,
    },
    {
      prompt: "Who's flagging tariff pressure on earnings calls?",
      icon: TravelExploreRounded,
    },
  ],
  // With a ticker: the specific question no screener or statement can answer.
  [
    { prompt: "How big is Oracle's revenue backlog?", icon: InsightsRounded },
    {
      prompt: "How many driverless trucks does Aurora have?",
      icon: InsightsRounded,
    },
    {
      prompt: "What did Nvidia say about data centre demand?",
      icon: InsightsRounded,
    },
  ],
];

/**
 * Everything else gets ONE slot between them, so the landing still hints at
 * breadth without spending its best real estate on table stakes.
 *
 * Plain fundamentals were dropped from the draw entirely (Ben, 2026-07-31:
 * "Break down Costco's valuation multiples" is "very basic"). Valuation and
 * cash-flow questions are answered well, they just do not persuade anyone, and
 * a card that reads like every other finance app wastes the slot. They keep
 * their section in ChatCapabilitiesSheet, where the job is completeness rather
 * than a first impression.
 */
const SUPPORTING_POOL: ChatPreset[][] = [
  [
    {
      prompt: "Show me the price target for Rocket Lab",
      icon: TrackChangesRounded,
    },
    {
      prompt: "What are analysts' price targets for Nebius?",
      icon: TrackChangesRounded,
    },
  ],
  [
    {
      prompt: "Show me Apple's revenue chart over five years",
      icon: CompareArrowsRounded,
    },
    {
      prompt: "Compare Microsoft and Alphabet's margins",
      icon: CompareArrowsRounded,
    },
  ],
  [
    {
      prompt: "What's on the economic calendar this week?",
      icon: CalendarMonthRounded,
    },
    {
      prompt: "When does Amazon next report earnings?",
      icon: CalendarMonthRounded,
    },
  ],
];

const pickOne = <T>(items: T[]): T =>
  items[Math.floor(Math.random() * items.length)];

/**
 * Both showcase capabilities plus one supporting capability, shuffled.
 *
 * Shuffling matters: without it the transcript pair would sit in the same two
 * positions on every visit and the third card would always be the odd one out,
 * which reads as a fixed menu rather than a rotating one.
 *
 * Call this from a `useState` initialiser, never in the render body. It must
 * stay fixed for the life of the screen (a fresh draw on every keystroke would
 * make the cards flicker) while still changing each time the user returns.
 */
export const pickPresets = (): ChatPreset[] => {
  const chosen = [
    ...SHOWCASE_POOL.map(pickOne),
    pickOne(SUPPORTING_POOL.map(pickOne)),
  ];
  for (let i = chosen.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [chosen[i], chosen[j]] = [chosen[j], chosen[i]];
  }
  return chosen;
};
